import UAParser from 'ua-parser-js'

import { baseGlobals } from '@root/typescript/baseGlobals'

import type { TOs, IResult, TBrowser } from './definitions'

import isFF          from './utils/isFF'
import isIE          from './utils/isIE'
import getOs         from './utils/getOs'
import isIOS         from './utils/isIOS'
import isEdge        from './utils/isEdge'
import isApple       from './utils/isApple'
import isOpera       from './utils/isOpera'
import isLinux       from './utils/isLinux'
import isChrome      from './utils/isChrome'
import isSafari      from './utils/isSafari'
import isWindows     from './utils/isWindows'
import getBrowser    from './utils/getBrowser'
import convertValues from './utils/convertValues'

export default class UserAgentParser {
  private resultCache?: UAParser.IResult

  /**
   * Browser verziószámának elkérése.
   * @param ua - UserAgent.
   */
  public getBrowserVersion (ua: string): string | undefined {
    return this.getResult(ua).browser.version
  }

  /**
   * Browser elkérése.
   * @param ua - UserAgent.
   */
  public getBrowser (ua: string): TBrowser {
    const { name } = this.getResult(ua).browser

    return getBrowser(name)
  }

  /**
   * Os elkérése, userAgent alapján.
   * @param ua - UserAgent.
   */
  public getOs (ua: string): TOs {
    const { name } = this.getResult(ua).os

    return getOs(name)
  }

  /**
   * Os verziószámának elkérése.
   * @param ua - UserAgent.
   */
  public getOsVersion (ua: string): string | undefined {
    return this.getResult(ua).os.version
  }

  /**
   * Chrome-ot használ a user.
   * @param ua - UserAgent.
   */
  public isChrome (ua: string): boolean {
    return isChrome(this.getBrowser(ua))
  }

  /**
   * Safarit használ a user.
   * @param ua - UserAgent.
   */
  public isSafari (ua: string): boolean {
    return isSafari(this.getBrowser(ua))
  }

  /**
   * Opera-t használ a user.
   * @param ua - UserAgent.
   */
  public isOpera (ua: string): boolean {
    return isOpera(this.getBrowser(ua))
  }

  /**
   * IE böngészőt használja a user.
   * @param ua - UserAgent.
   */
  public isIE (ua: string): boolean { // eslint-disable-line @typescript-eslint/naming-convention
    return isIE(this.getBrowser(ua))
  }

  /**
   * Edge böngészőt használ a user.
   * @param ua - UserAgent.
   */
  public isEdge (ua: string): boolean {
    return isEdge(this.getBrowser(ua))
  }

  /**
   * Firefox-ot használ a user.
   * @param ua - UserAgent.
   */
  public isFF (ua: string): boolean { // eslint-disable-line @typescript-eslint/naming-convention
    return isFF(this.getBrowser(ua))
  }

  /**
   * Ez egy android eszköz, vagy CORDOVA-val buildelünk?
   * @param ua - UserAgent.
   */
  public isAndroid (ua: string): boolean {
    return this.getOs(ua) === 'android'
  }

  /**
   * Ez egy mobil eszköz, vagy CORDOVA-val buildelünk?
   * @param ua - UserAgent.
   */
  public isMobile (ua: string): boolean {
    const { device } = this.getResult(ua)

    return device.type === 'mobile'
  }

  /**
   * Az OS Linux?
   * @param ua - UserAgent.
   */
  public isLinux (ua: string): boolean {
    return isLinux(this.getOs(ua))
  }

  /**
   * Az OS Windows?
   * @param ua - UserAgent.
   */
  public isWindows (ua: string): boolean {
    return isWindows(this.getOs(ua))
  }

  /**
   * Az eszköz Tablet?
   * @param ua - UserAgent.
   */
  public isTablet (ua: string): boolean {
    const { device } = this.getResult(ua)

    return device.type === 'tablet'
  }

  /**
   * Ez egy Apple eszköz?
   * @param ua - UserAgent.
   */
  public isApple (ua: string): boolean {
    return isApple(this.getOs(ua))
  }

  /**
   * Ez egy iOS eszköz?
   * @param ua - UserAgent.
   */
  public isIOS (ua: string): boolean { // eslint-disable-line @typescript-eslint/naming-convention
    return isIOS(this.getOs(ua))
  }

  /**
   * BrowserInfo elkérése.
   * @param ua - UserAgent.
   */
  public getResult (ua: string): IResult {
    // FONTOS, TESZT alatt nincs CACHE.
    if (!baseGlobals.isTest && this.resultCache) {
      return this.resultCache
    }

    const parser = new UAParser(ua)

    const result = parser.getResult()

    convertValues(result)

    this.resultCache = result

    return result
  }
}

export const userAgentParser = new UserAgentParser()
